import {setUserProfileBasedOnGaiaInfo} from '@/app/signup/(state)/UserProfileState';
import {getAnalytics} from '@/hooks/useAnalytics';
import type {
  CurrentStep,
  GetNextBuyflowStepRequest,
  GetNextBuyflowStepResponse,
} from '@/lib/TypesOpenApi';
import {getOpenApiBaseUrl} from '@/lib/open-api';
import type {paths} from '@/lib/typings/buyflowservice/buyflowservice';
import {
  type GoogleUserProfile,
  getMockedJwtFromSessionStorage,
  isCiamEnabled,
  isRequestMockingEnabled,
  isRequestedFromGoogleProxy,
} from '@gfiber/pixel-party';
import {fetchPost} from './fetchPost';
import {getAuth0Token} from '@/lib/utils/auth0TokenStorage';

export const fetchBuyflowData = async (
  globalAddrId: string,
  request: GetNextBuyflowStepRequest,
): Promise<GetNextBuyflowStepResponse> => {
  if (isRequestMockingEnabled()) {
    return fetchLocalMock(globalAddrId, request);
  }

  const analytics = getAnalytics();
  const currentStep = request.buyflowState.currentStep;
  const {data, response} = await fetchPost<paths>({
    baseUrl: getOpenApiBaseUrl()!,
    path: '/v1/buyflow',
    globalAddrId,
    body: request as any,
    authHeader: getAuthJwt(currentStep) as string,
  });

  analytics.setField(
    'internalUser',
    (response.headers.get('X-Gfiber-Internal-User') ?? '').toLowerCase() ===
      'true',
  );

  if (!isCiamEnabled()) {
    /*
     * Retrieve gaia info from BFF response header.
     * Set here directly since gaia info return in header
     * and not defined within GetNextBuyflowStepResponse type.
     */
    // TODO: need to clarify what to do for backward flow.
    // We cache data and if the data in the cache we will not call BFF.
    // we use queryClient to cache request to fetch buyflow step data.
    const googleUserProfile: GoogleUserProfile = {
      givenName: response.headers.get('X-GFiber-First-Name') ?? '',
      familyName: response.headers.get('X-GFiber-Last-Name') ?? '',
      name: response.headers.get('X-GFiber-Name') ?? '',
      email: response.headers.get('X-GFiber-Email') ?? '',
      picture: response.headers.get('X-GFiber-Picture') ?? '',
      largePicture: response.headers.get('X-Gfiber-Picture-Large') ?? '',

      // TODO: remove hard coding values after below fields defined in BFF.
      emailVerified: 'true',
      locale: 'en_US',
      isExpired: false,
    };

    setUserProfileBasedOnGaiaInfo(googleUserProfile);
  }

  return data;
};

async function fetchLocalMock(
  globalAddrId: string,
  request: GetNextBuyflowStepRequest,
): Promise<GetNextBuyflowStepResponse> {
  // Log to allow investigate body during development
  console.log('Buyflow request body', globalAddrId, request);
  const response = await fetch(
    'http://localhost:3000/buyflow/' + request.buyflowState.currentStep,
  );
  const data = await response.json();

  if (data?.buyflowState.serviceAddressInfo?.globalAddressId) {
    // override to avoid infinite loop that caused by state reset
    data.buyflowState.serviceAddressInfo.globalAddressId = globalAddrId;
  }

  return data;
}
const AUTH0_TOKEN_PAGES: CurrentStep[] = [
  'SIGN_IN',
  'SIGN_IN_OTP',
  'VERIFY',
  'BILLING',
  'NOTIFICATION_PREFERENCES',
  'END_BUYFLOW',
];
export const getAuthJwt = (currentStep: CurrentStep) => {
  // TODO: Remove below testing mock once GFiber token is ready.
  if (
    !isRequestedFromGoogleProxy() &&
    !isCiamEnabled() &&
    (currentStep === 'SIGN_IN' ||
      currentStep === 'VERIFY' ||
      currentStep === 'BILLING' ||
      currentStep === 'NOTIFICATION_PREFERENCES' ||
      currentStep === 'END_BUYFLOW')
  ) {
    return 'Bearer eyJqa3UiOiJodHRwczovL2FwaS5kZXYuZ2ZpYmVyLmNvbS9idXlmbG93LWp3a3MvcmV0cmlldmUtandrcyIsImtpZCI6InJzYV9fYWJ2cGg1OXV1djdwZjM2d2QyIiwidHlwIjoiSldUIiwiYWxnIjoiUlMyNTYifQ.eyJzdWIiOiJyaXJyaWJhcnJlbkBnb29nbGUuY29tIiwiYXVkIjoic3ZjLmRldi5nZmliZXIuY29tIiwicm9sZSI6WyJSRVNJX1BST1NQRUNUIl0sImlzcyI6Imh0dHBzOi8vYXBpLmRldi5nZmliZXIuY29tL3YxL3NlY3VyZS9jeGUvdjEvYnV5ZmxvdyIsImlkIjoiNDU2NTY3Njc4IiwiaWF0IjoxNzE2OTgyMDE0LCJqdGkiOiIzNTU2YmRmNi05MDI0LTQ4NDUtYjU3OS0yN2ZkNjcxNzU0MTMifQ.LoVdy7i-Ri-r6OFqC1INsk_LhY6d2Zm0J7JZC0vwG9X6Sq9QXXJGpBJLNyx8HlbZpSW-ZCZ3mCaF3IX-QEJqEEM5LU4G-uej0DO3XLY97yO8nQb4salQwbhlJTX0UWNB0XFhjbUfpmxeBqK4PYQP2AWLdLqg_e9bVLhVHb8vuZHkUWKk6L1s3O4xK7sdGA3wDVtAPZWb5ty8SvHc0OZ_vHMfUm4UzfJcBwwhRFpAZ6o4dDj_3BALO9N5_-xky3K__rOi9k4EyVO4k-Z7uGV3Sf14MokBCOIDCuAlZ_XWr6f0MEq2vpWeHlM19COfj6LTIpsHz22qea5SKaN4UKkavQ';
  } else if (isCiamEnabled() && AUTH0_TOKEN_PAGES.includes(currentStep)) {
    return getAuth0Token();
  }

  if (getMockedJwtFromSessionStorage()) {
    return getMockedJwtFromSessionStorage() as string;
  }
};
